import React, { useCallback, useEffect, useState } from "react"
import "./ContestPreviousWinners.scss"
import { useAppContext } from "../../../hooks/appContext"
import { Sizes } from "../../../interfaces/sizing"
import { generateApiPath } from "../../../routes/util"
import { error } from "../../../utils/debug"
import { fetchGet } from "../../../utils/myFetch"
import { Typography } from "../../common"
import { ContestPreviousWinnersTable } from "../ContestPreviousWinnersTable"
import { DateForm } from "../DateForm"

interface GetPreviousWinnersResponse {
    /** List of winners */
    winners: IPreviousWinner[]
}

export interface IPreviousWinner {
    /** The time of the date when the user wins the contest */
    time: string
    /** The user's image url */
    image_url?: string
    /** The user's username */
    username?: string
    /** The prize money won by the user */
    prize_amount?: string
}

interface ContestPreviousWinnersProps {
    /** The initial date to fetch the previous winners data, in YYYY-M-D format. */
    initialDate?: string
}

export function ContestPreviousWinners({
    initialDate,
}: ContestPreviousWinnersProps) {
    const [isLoading, setIsLoading] = useState(true)
    const [previousWinnersData, setPreviousWinnersData] = useState<
        IPreviousWinner[]
    >([])
    const [selectedDate, setSelectedDate] = useState<string>(
        () => initialDate ?? "",
    )
    const languageCode = useAppContext().context.language_code
    const contestPreviousWinnerApiPath = `${generateApiPath(
        languageCode,
        "contest",
        "previous_winners",
    )}`

    const onDateFormSubmit = (date: string) => {
        setSelectedDate(date)
        loadPreviousWinners(date, false)
    }

    /**
     * Fetches previous winners data from the contest API.
     * @param date the date we ought to fetch previous winners for. has to be in YYYYY-M-D format.
     * @param setLoading whether we need to set the loading state when we call this. We only set the loading state during the initial useEffect call.
     */
    const loadPreviousWinners = useCallback(
        async (
            date: string | undefined = undefined,
            setLoading = true,
        ): Promise<void> => {
            setLoading && setIsLoading(true)
            try {
                const response = await fetchGet(
                    `${contestPreviousWinnerApiPath}${
                        date ? `?date=${date}` : ""
                    }`,
                )
                const parsedResponse =
                    response.jsonData as GetPreviousWinnersResponse
                const winners = parsedResponse.winners
                setPreviousWinnersData(winners)
            } catch (err) {
                error(err)
            }
            setLoading && setIsLoading(false)
        },
        [contestPreviousWinnerApiPath],
    )

    useEffect(() => {
        loadPreviousWinners()
    }, [loadPreviousWinners])
    return (
        <div className="ContestPreviousWinners">
            {isLoading ? (
                <Typography size={Sizes.xs}>Loading..</Typography>
            ) : (
                <>
                    <DateForm
                        onFormSubmit={onDateFormSubmit}
                        initialDate={initialDate}
                    />
                    <hr />
                    <ContestPreviousWinnersTable
                        selectedDate={selectedDate}
                        previousWinners={previousWinnersData}
                    />
                </>
            )}
        </div>
    )
}
